import { Quote } from '@/model/quotesModel';
import { parseInflationPercent } from '@/utils/inflationParser';
import { parseDurationYears as parseBenefitPeriod } from '@/utils/benefitPeriod';

export const MAX_DURATION_YEARS = 9;

export function matchesTab(quote: Quote, tab: ShopInsuranceTab): boolean {
  if (tab === 'joint') return quote.policyType === 'Joint Life';
  if (tab === 'couples') return quote.policyType === 'Couples Discount';
  return quote.policyType === 'Single Life' || quote.policyType === 'Standard';
}

export function parseDurationYearsAsNumber(value?: string): number {
  const result = parseBenefitPeriod(value);
  return result === 'lifetime' ? Infinity : result;
}

/**
 * Checks if a numeric value falls within a min-max range.
 *
 * Special case: Infinity (representing "Lifetime" duration) is treated as
 * within range if the max value equals the slider's maximum setting.
 * This allows users to include/exclude Lifetime products by adjusting the slider.
 */
export function inRange(value: number, min: number, max: number, sliderMax?: number): boolean {
  if (value === Infinity) return sliderMax !== undefined && max >= sliderMax;
  return value >= min && value <= max;
}

export function getInflationProtection(quote: Quote) {
  const hasSingle = !!quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None';

  if (hasSingle) {
    const rate = parseInflationPercent(quote.inflationBenefitOption);
    return { hasSingle: true, single: rate, aob: rate, cob: rate };
  }

  return {
    hasSingle: false,
    single: undefined as number | undefined,
    aob: parseInflationPercent(quote.aobInflation),
    cob: parseInflationPercent(quote.cobInflation),
  };
}

export function getBenefitsDuration(quote: Quote) {
  const aobYears = parseDurationYearsAsNumber(quote.aobDuration);
  const cobYears = parseDurationYearsAsNumber(quote.cobDuration);

  if (aobYears || cobYears) {
    const totalYears =
      aobYears === Infinity || cobYears === Infinity ? Infinity : aobYears + cobYears;
    return { aobYears, cobYears, totalYears };
  }

  const totalYears = (quote.ltcBenefitDurationMonths ?? 0) / 12;
  return { aobYears: 0, cobYears: 0, totalYears };
}
